<template>
    <div class="venue__edit_form">
        <form-text
                :label="$t('ebs.venues.edit.labels.name')"
                :name="$t('ebs.venues.edit.name.name')"
                :required="true"
                :v-validate="'required|max:255'"
                :disabled="!venueAllowEdit"
                :data-vv-as="$t('forms.dataVvAs.name')"
                v-model="venueName"
        />
        <form-textarea
                :label="$t('ebs.venues.edit.labels.description')"
                :name="$t('ebs.venues.edit.name.description')"
                :v-validate="'max:2000'"
                :disabled="!venueAllowEdit"
                v-model="venueDescription"
        />
        <form-image
                :label="$t('ebs.venues.edit.labels.images')"
                :name="$t('ebs.venues.edit.name.images')"
                :value="venueImages"
                :multiple="true"
                :disabled="!venueAllowEdit"
                @emit-select="venueUploadImages"
                @emit-remove="venueRemoveImages"
        />
        <form-location
            ref="formlocation"
            :name="'venue-location'"
            :value-address1="venueAddressAddress1"
            :value-address2="venueAddressAddress2"
            :value-city="venueAddressCity"
            :value-county="venueAddressCounty"
            :value-country="venueAddressCountry"
            :value-postcode="venueAddressPostcode"
            :value-type="venueAddressType"
            :value-formatted="venueAddressFormatted"
            :disabled="!venueAllowEdit"
            :data-vv-as="$t('forms.dataVvAs.address')"
            @emit-update-value-address1="venueUpdateAddressAddress1"
            @emit-update-value-address2="venueUpdateAddressAddress2"
            @emit-update-value-city="venueUpdateAddressCity"
            @emit-update-value-county="venueUpdateAddressCounty"
            @emit-update-value-country="venueUpdateAddressCountry"
            @emit-update-value-postcode="venueUpdateAddressPostcode"
            @emit-type="venueUpdateAddressType"
            @emit-change="venueUpdateAddress"
        />
        <component-title :size="'medium'">{{ $t('ebs.venues.edit.title.contactInformation') }}</component-title>
        <form-email
            :label="$t('ebs.venues.edit.labels.email')"
            :name="$t('ebs.venues.edit.name.email')"
            :disabled="!venueAllowEdit"
            v-validate="'email|max:55'"
            :data-vv-as="$t('forms.dataVvAs.email')"
            v-model="venueContactEmail"
        />
        <form-phone
            :label="$t('ebs.venues.edit.labels.phone')"
            :name="$t('ebs.venues.edit.name.phone')"
            :disabled="!venueAllowEdit"
            v-model="venueContactPhone"
        />
        <div class="venue__edit_form_box" v-if="!isSecondStageTokenFromOldLMS">
            <component-title :size="'medium'">{{ $t('ebs.venues.edit.title.sharingTitle') }}</component-title>
            <form-radio-horizontal
                class="venue__edit_form_sharing"
                v-for="(workspace, index) in customerWorkspacesList"
                :key="index"
                :label="workspace.name"
                :name="workspace.id"
                :items="[
                    { name: $t('ebs.venues.edit.name.visible'), label: workspace.id, value: true },
                    { name: $t('ebs.venues.edit.name.hidden'), label: workspace.id, value: false }
                ]"
                :disabled="workspace.id === accessedWorkspace || !venueAllowEdit"
                :value="venueForWorkspaces.includes(workspace.id)"
                @input="setVenueSharingWorkspaces(workspace.id, ...arguments)"
            >
            </form-radio-horizontal>
        </div>
        <component-title :size="'medium'">{{ $t('ebs.venues.edit.title.visibility') }}</component-title>
        <form-checkbox
            class="venue__edit_form_status"
            :label="'Status'"
            :name="'status'"
            :switcher="true"
            :activeInactive="true"
            :disabled="!venueAllowEdit"
            :value="venueStatus === 'active'"
            @input="updateVenueStatus"
        >{{ $t('ebs.venues.edit.textActiveVenues') }}
        </form-checkbox>
        <form-submit
            :disabled="!venueAllowEdit"
            @reset="venueReset"
            @submit="performSubmitValidate"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import FormTextarea from '@/components/default/forms/FormTextarea'
import FormImage from '@/components/default/forms/FormImage'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormLocation from '@/components/default/forms/FormLocation'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import FormEmail from '@/components/default/forms/FormEmail'
import FormPhone from '@/components/default/forms/FormPhone'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import { performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'

export default {
  name: 'venue-edit-form',
  mixins: [validateBeforeRouteLeave, performSubmitValidate, watchContentChangeEmit],
  components: {
    FormPhone,
    FormEmail,
    PopNotify,
    FormSubmit,
    FormLocation,
    FormRadioHorizontal,
    ComponentTitle,
    FormImage,
    FormTextarea,
    FormText,
    FormCheckbox
  },
  props: {
    status: {
      type: String,
      default: 'active'
    }
  },
  computed: {
    isSecondStageTokenFromOldLMS () {
      return this.$store.getters.getAuthUserIsSecondStageTokenOldLMS
    },
    customerWorkspacesList: function () {
      return this.$store.getters.getLmsUserCustomerworkspaces
    },
    accessedWorkspace () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    venueForWorkspaces: function () {
      return this.$store.getters.getVenueSharingWorkspaces
    },
    customerId: function () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    venueId: function () {
      return this.$store.getters['getVenueId']
    },
    venueName: {
      get () {
        return this.$store.getters['getVenueName']
      },
      set (value) {
        this.$store.dispatch('setVenueName', value)
      }
    },
    venueDescription: {
      get () {
        return this.$store.getters['getVenueDescription']
      },
      set (value) {
        this.$store.dispatch('setVenueDescription', value)
      }
    },
    venueContactEmail: {
      get () {
        return this.$store.getters['getVenueContactEmail']
      },
      set (value) {
        this.$store.dispatch('setVenueContactEmail', value)
      }
    },
    venueContactPhone: {
      get () {
        return this.$store.getters['getVenueContactPhone']
      },
      set (value) {
        this.$store.dispatch('setVenueContactPhone', value)
      }
    },
    venueAddressAddress1: {
      get () {
        return this.$store.getters['getVenueAddressAddress1']
      }
    },
    venueAddressAddress2: {
      get () {
        return this.$store.getters['getVenueAddressAddress2']
      }
    },
    venueAddressCity: {
      get () {
        return this.$store.getters['getVenueAddressCity']
      }
    },
    venueAddressCountry: {
      get () {
        return this.$store.getters['getVenueAddressCountry']
      }
    },
    venueAddressCounty: {
      get () {
        return this.$store.getters['getVenueAddressCounty']
      }
    },
    venueAddressPostcode: {
      get () {
        return this.$store.getters['getVenueAddressPostcode']
      }
    },
    venueAddressFormatted: {
      get () {
        return this.$store.getters['getVenueAddressFormatted']
      }
    },
    venueAddressType: {
      get () {
        return this.$store.getters['getVenueAddressType']
      }
    },
    venueImages: {
      get () {
        return this.$store.getters['getVenueImages']
      }
    },
    venueStatus: {
      get () {
        return this.$store.getters['getVenueStatus']
      }
    },
    watchAddressFields () {
      return this.venueAddressAddress1 + this.venueAddressAddress2 + this.venueAddressCity + this.venueAddressCountry + this.venueAddressCounty + this.venueAddressPostcode
    },
    venueAllowEdit () {
      return this.$store.getters.getVenueAllowEdit
    },
    watchContent () {
      return this.venueName + this.venueDescription + this.venueContactEmail +
      this.venueContactPhone + this.venueAddressAddress1 + this.venueAddressAddress2 +
      this.venueAddressCity + this.venueAddressCountry + this.venueAddressCounty +
      this.venueAddressPostcode + this.venueAddressFormatted + this.venueAddressType +
      this.venueImages + this.venueStatus
    },
    //
    rootItemName () {
      return 'venue'
    },
    nameActionCreate () {
      return 'createVenue'
    },
    nameActionUpdate () {
      return 'updateVenue'
    }
  },
  watch: {
    watchAddressFields () {
      if (this.venueAddressType === 'manual') {
        this.$store.dispatch('setVenueAddressFormattedaddress', '')
        this.$store.dispatch('setVenueAddressLat', '')
        this.$store.dispatch('setVenueAddressLng', '')
      }
    }
  },
  methods: {
    getLmsCustomerWorkspaces () {
      return this.$store.dispatch('lmsUserCustomerworkspacesGet', { customerId: this.customerId, status: this.status })
    },
    setVenueSharingWorkspaces (id, value) {
      this.$store.dispatch('setVenueSharingWorkspaces', { id, value })
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('venue-submit')
    },
    //
    venueUploadImages: function (data) {
      this.$store.dispatch('setVenueImages', data)
    },
    venueRemoveImages: function (key) {
      this.$store.dispatch('removeVenueImages', key)
    },
    venueUpdateAddress: function (data) {
      for (let key in data) {
        if (data.hasOwnProperty(key)) {
          switch (key) {
            case 'route':
              this.$store.dispatch('setVenueAddressAddress1', data[key])
              break
            case 'street_number':
              this.$store.dispatch('setVenueAddressAddress2', data[key])
              break
            case 'postal_town':
              this.$store.dispatch('setVenueAddressCity', data[key])
              break
            case 'country':
              this.$store.dispatch('setVenueAddressCountry', data[key])
              break
            case 'administrative_area_level_1':
              this.$store.dispatch('setVenueAddressCounty', data[key])
              break
            case 'formatted_address':
              this.$store.dispatch('setVenueAddressFormattedaddress', data[key])
              break
            case 'lat':
              this.$store.dispatch('setVenueAddressLat', data[key])
              break
            case 'lng':
              this.$store.dispatch('setVenueAddressLng', data[key])
              break
            case 'postal_code':
              this.$store.dispatch('setVenueAddressPostcode', data[key])
              break
            default:
              break
          }
        }
      }
    },
    venueUpdateAddressAddress1: function () {
      this.$store.dispatch('setVenueAddressAddress1', event.target.value)
    },
    venueUpdateAddressAddress2: function () {
      this.$store.dispatch('setVenueAddressAddress2', event.target.value)
    },
    venueUpdateAddressCity: function () {
      this.$store.dispatch('setVenueAddressCity', event.target.value)
    },
    venueUpdateAddressCountry: function (value) {
      this.$store.dispatch('setVenueAddressCountry', value)
    },
    venueUpdateAddressCounty: function () {
      this.$store.dispatch('setVenueAddressCounty', event.target.value)
    },
    venueUpdateAddressPostcode: function () {
      this.$store.dispatch('setVenueAddressPostcode', event.target.value)
    },
    venueUpdateAddressType: function (value) {
      this.$store.dispatch('setVenueAddressType', value)
    },
    //
    updateVenueStatus (value) {
      let status = value ? 'active' : 'draft'
      this.$store.dispatch('setVenueStatus', status)
    },
    venueReset: function () {
      this.$store.dispatch('resetVenue')
      this.$refs.formlocation.resetLocation()
    }
  },
  created () {
    if (!this.isSecondStageTokenFromOldLMS) {
      this.getLmsCustomerWorkspaces().then(() => {
        this.setVenueSharingWorkspaces(this.accessedWorkspace, true)
      })
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/ebs/venues/venueaddedit";
</style>
